import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { BackButton } from "src/components/base/button";
import TabLayout from "src/components/base/tab-layout";
import { Tab, TabMenu } from "src/components/base/tab-menu";
import React, { Suspense } from "react";
import SINGLE_DEVICE_GROUP_CONTEXT, { SingleDeviceGroupProvider } from "src/context/internal/device-group";
import {
    Dropdown,
    DropdownButton,
    DropdownHeading,
    DropdownItem,
    DropdownLabel,
    DropdownMenu,
    DropdownSection,
} from "src/components/base/dropdown";
import { EllipsisVerticalIcon, LinkIcon, PencilSquareIcon, TrashIcon } from "@heroicons/react/20/solid";
import { SimpleDeviceGroup } from "src/api/generated/frontend_internal_v1";
import CreateDeviceGroupDialog from "src/components/dialogs/create-device-group";
import RenameDeviceGroupDialog from "src/components/dialogs/rename-device-group";
import ChangeDeviceGroupDescDialog from "src/components/dialogs/change-device-group-desc";
import DeleteDeviceGroupDialog from "src/components/dialogs/delete-device-group";
import DeviceGroupLinkDeviceDialog from "src/components/dialogs/device-group-link-device";
import Outlet from "src/components/base/outlet";

/**
 * The properties for {@link SingleDeviceGroupView}
 */
export type DeviceGroupLayout = {};

/**
 * The Layout for a single device group
 */
function SingleDeviceGroupView(props: DeviceGroupLayout) {
    const [t] = useTranslation("admin-device-group");

    const { deviceGroupId } = Route.useParams();

    const [openCreateDeviceGroup, setOpenCreateDeviceGroup] = React.useState(false);
    const [openRenameDeviceGroup, setOpenRenameDeviceGroup] = React.useState<SimpleDeviceGroup>();
    const [openSetDeviceGroupDesc, setOpenSetDeviceGroupDesc] = React.useState<SimpleDeviceGroup>();
    const [openDeleteDeviceGroup, setOpenDeleteDeviceGroup] = React.useState<SimpleDeviceGroup>();

    const [openLinkNewDevice, setOpenLinkNewDevice] = React.useState(false);

    const navigate = useNavigate();

    return (
        <SingleDeviceGroupProvider uuid={deviceGroupId}>
            <SINGLE_DEVICE_GROUP_CONTEXT.Consumer>
                {(ctx) => (
                    <div className={"flex flex-col gap-6"}>
                        <BackButton className={"self-start"} href={"/a/devicegroups"}>
                            {t("button.back-to-device-group-overview")}
                        </BackButton>

                        <TabLayout
                            heading={t("heading.device-group", { deviceGroup: ctx.deviceGroup.name })}
                            headingChildren={
                                <Dropdown>
                                    <DropdownButton plain={true}>
                                        <EllipsisVerticalIcon />
                                    </DropdownButton>
                                    <DropdownMenu anchor={"bottom end"}>
                                        <DropdownSection>
                                            <DropdownItem onClick={() => setOpenRenameDeviceGroup(ctx.deviceGroup)}>
                                                <PencilSquareIcon />
                                                <DropdownLabel>{t("button.rename-device-group")}</DropdownLabel>
                                            </DropdownItem>
                                            <DropdownItem onClick={() => setOpenSetDeviceGroupDesc(ctx.deviceGroup)}>
                                                <PencilSquareIcon />
                                                <DropdownLabel>
                                                    {t("button.set-device-group-description")}
                                                </DropdownLabel>
                                            </DropdownItem>
                                            <DropdownItem onClick={() => setOpenLinkNewDevice(true)}>
                                                <LinkIcon />
                                                {t("button.add-device-to-group")}
                                            </DropdownItem>
                                        </DropdownSection>
                                        <DropdownSection>
                                            <DropdownHeading>{t("heading.danger-zone")}</DropdownHeading>
                                            <DropdownItem onClick={() => setOpenDeleteDeviceGroup(ctx.deviceGroup)}>
                                                <TrashIcon />
                                                <DropdownLabel>{t("button.delete-device-group")}</DropdownLabel>
                                            </DropdownItem>
                                        </DropdownSection>
                                    </DropdownMenu>
                                </Dropdown>
                            }
                            tabs={
                                <TabMenu>
                                    <Tab href={"/a/devicegroups/$deviceGroupId/associated"} params={{ deviceGroupId }}>
                                        {t("label.associated-devices")}
                                    </Tab>
                                    <Tab
                                        href={"/a/devicegroups/$deviceGroupId/inbound-acls"}
                                        params={{ deviceGroupId }}
                                    >
                                        {t("label.inbound-acls")}
                                    </Tab>
                                    <Tab
                                        href={"/a/devicegroups/$deviceGroupId/outbound-acls"}
                                        params={{ deviceGroupId }}
                                    >
                                        {t("label.outbound-acls")}
                                    </Tab>
                                </TabMenu>
                            }
                        >
                            <Outlet />
                        </TabLayout>

                        <Suspense>
                            <CreateDeviceGroupDialog
                                isOpen={openCreateDeviceGroup}
                                onClose={() => setOpenCreateDeviceGroup(false)}
                            />

                            <RenameDeviceGroupDialog
                                deviceGroup={openRenameDeviceGroup}
                                isOpen={!!openRenameDeviceGroup}
                                onClose={() => setOpenRenameDeviceGroup(undefined)}
                                onSubmit={() => {
                                    setOpenRenameDeviceGroup(undefined);
                                    ctx.reset();
                                }}
                            />

                            <ChangeDeviceGroupDescDialog
                                deviceGroup={openSetDeviceGroupDesc}
                                isOpen={!!openSetDeviceGroupDesc}
                                onClose={() => setOpenSetDeviceGroupDesc(undefined)}
                                onSubmit={async () => setOpenSetDeviceGroupDesc(undefined)}
                            />

                            <DeleteDeviceGroupDialog
                                deviceGroup={openDeleteDeviceGroup}
                                isOpen={!!openDeleteDeviceGroup}
                                onDeletion={async () => {
                                    setOpenDeleteDeviceGroup(undefined);
                                    await navigate({ to: "/a/devicegroups", search: { search: "", page: 1 } });
                                }}
                                onClose={() => setOpenDeleteDeviceGroup(undefined)}
                            />

                            <DeviceGroupLinkDeviceDialog
                                deviceGroup={ctx.deviceGroup}
                                isOpen={openLinkNewDevice}
                                onClose={() => setOpenLinkNewDevice(false)}
                                onSuccess={async () => {
                                    setOpenLinkNewDevice(false);
                                    ctx.reset();
                                }}
                            />
                        </Suspense>
                    </div>
                )}
            </SINGLE_DEVICE_GROUP_CONTEXT.Consumer>
        </SingleDeviceGroupProvider>
    );
}

export const Route = createFileRoute("/_admin/a/devicegroups/$deviceGroupId/_devicegroups")({
    component: SingleDeviceGroupView,
});
